import { PopoverTag, ConfirmationDialog } from '@acp/core-ui';
import {
    Box, MenuDivider, MenuItem, Text,
} from '@chakra-ui/react';
import { ReportedFindingData } from '@acp/shared';
import { parseISO, format } from 'date-fns';
import { ReportedFindingsMachineContext } from '../../reported-findings/components/wizard/machine/context';

function Confirmation({ reportedFindingId }: { reportedFindingId: number }) {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const dismissFinding = ReportedFindingsMachineContext.useSelector((state) => state.matches('dismissFinding'));

    const isLoading = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches({ dismissFinding: 'dismissing' })
            || state.matches({ dismissFinding: 'unDismissing' })
    );

    const reportedFindings = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.reportedFindings
    );

    const reportedFinding = reportedFindings.find(
        (x) => x.problemRegister.problemRegisterId === reportedFindingId
    );

    const isDismissed = reportedFinding?.problemRegister?.isDismissed === true;

    const confirmText = isDismissed
        ? 'This action will restore the finding and make it visible again in the actionable reported findings pages.'
        : 'This action will dismiss the finding and make it hidden from the actionable reported findings pages.';

    return (
        <ConfirmationDialog
            isOpen={dismissFinding}
            title="Are you sure?"
            confirmText={isDismissed ? 'Restore' : 'Dismiss'}
            cancelText="Back"
            onCancel={() => machine.send({ type: 'BACK' })}
            onConfirm={() => machine.send({
                type: isDismissed ? 'UN_DISMISS_FINDING' : 'DISMISS_FINDING',
                reportedFindingId,
            })}
            isLoading={isLoading}
            isCentered
            trapFocus={false}
        >
            {confirmText}
        </ConfirmationDialog>
    );
}

export function DismissFindingModalUI() {
    const reportedFindingId = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.dismissReportedFindingId
    );

    const dismissFinding = ReportedFindingsMachineContext.useSelector((state) => state.matches('dismissFinding'));

    if (dismissFinding && reportedFindingId != null) {
        return <Confirmation reportedFindingId={reportedFindingId} />;
    }

    return null;
}

export function FindingDismissedTag({ reportedFinding }: { reportedFinding: ReportedFindingData }) {
    if (reportedFinding.problemRegister?.isDismissed !== true) {
        return null;
    }

    return (
        <Box mt={1}>
            <PopoverTag popoverPlacement="auto" colorScheme="blackAlpha" title="Finding dismissed">
                <Text fontSize="small">Finding dismissed by</Text>
                <Text fontWeight="bold" fontSize="small" color="muted.500">
                    {reportedFinding.problemRegister?.dismissedBy ?? ''}
                </Text>
                <Text fontWeight="bold" fontSize="small" color="muted.500">
                    {
                        // eslint-disable-next-line max-len
                        reportedFinding.problemRegister?.dateDismissed != null
                            ? format(
                                parseISO(reportedFinding.problemRegister.dateDismissed),
                                'dd/MM/yyyy HH:mm:ss a'
                            )
                            : ''
                    }
                </Text>
            </PopoverTag>
        </Box>
    );
}

export function DismissFindingButton({
    reportedFinding,
}: {
    reportedFinding: ReportedFindingData;
}) {
    const machine = ReportedFindingsMachineContext.useActorRef();

    if (reportedFinding.problemRegister?.isDismissible === true) {
        return (
            <>
                <MenuDivider />
                <MenuItem
                    textColor="red"
                    onClick={() => machine.send({
                        type: 'DISMISS_FINDING',
                        reportedFindingId: reportedFinding.problemRegister.problemRegisterId,
                    })}
                >
                    Dismiss finding
                </MenuItem>
            </>
        );
    }

    if (reportedFinding.problemRegister?.isDismissed === true) {
        return (
            <>
                <MenuDivider />
                <MenuItem
                    onClick={() => machine.send({
                        type: 'DISMISS_FINDING',
                        reportedFindingId: reportedFinding.problemRegister.problemRegisterId,
                    })}
                >
                    Restore finding
                </MenuItem>
            </>
        );
    }

    return null;
}
